import { useState, useEffect, useCallback } from 'react';
import { Card, Table, Tabs, Empty, Button, Typography, Tag, Space, Modal, Collapse, Descriptions, message, Checkbox, Select } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { useEvent } from '../../hooks/useEvent';
import { getEventSource } from '../../utils/eventSource';
import forecastService, {
  type ForecastDaily,
  type ForecastHourly,
  type ForecastMinutely,
  type DailyForecast,
  type HourlyForecast,
  type MinutelyForecast,
  type DailyMetadata,
} from '../../api/forecastService';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

const { Text } = Typography;

type ForecastType = 'daily' | 'hourly' | 'minutely';
type ForecastRecord = ForecastDaily | ForecastHourly | ForecastMinutely;
type StatusFilter = 'all' | 'draft' | 'published';

function getDisplayStatus(forecast: ForecastRecord): 'deleted' | 'draft' | 'published' {
  if (forecast.deleted) return 'deleted';
  const status = (forecast as ForecastDaily | ForecastHourly).status;
  if (status === 'draft') return 'draft';
  return 'published';
}

function renderStatusTag(forecast: ForecastRecord, isLegacy: boolean) {
  const display = getDisplayStatus(forecast);
  if (display === 'deleted') return <Tag color="red">Deleted</Tag>;
  if (isLegacy) return null;
  if (display === 'draft') return <Tag color="orange">Draft</Tag>;
  return <Tag color="green">Published</Tag>;
}

// Cell wrapper for word-wrapping text
const wrapStyle: React.CSSProperties = {
  whiteSpace: 'normal',
  wordBreak: 'break-word',
};

function getTimestampString(ts: string | { value?: string }): string {
  return typeof ts === 'string' ? ts : ts?.value ?? '';
}

function getForecastTypeLabel(type: ForecastType): string {
  return type.charAt(0).toUpperCase() + type.slice(1);
}

function ForecastDetailContent({
  type,
  forecast,
  formatTimestamp,
  wrapStyle: ws,
}: {
  type: ForecastType;
  forecast: ForecastRecord;
  formatTimestamp: (ts: string) => string;
  wrapStyle: React.CSSProperties;
}) {
  if (type === 'daily') {
    const fd = forecast as ForecastDaily;
    return (
      <div style={{ padding: '8px 0' }}>
        <Descriptions column={1} size="small" bordered>
          <Descriptions.Item label="Create Datetime">{formatTimestamp(fd.create_datetime)}</Descriptions.Item>
          <Descriptions.Item label="Value Date">{formatTimestamp(fd.value_date)}</Descriptions.Item>
          <Descriptions.Item label="General Situation">
            <div style={ws}>{fd.general_situation || '-'}</div>
          </Descriptions.Item>
        </Descriptions>
        <div style={{ marginTop: 16 }}>
          <div style={{ fontWeight: 600, marginBottom: 8 }}>Daily Forecasts</div>
          <Collapse
            size="small"
            items={(fd.daily_forecasts || []).map((df: DailyForecast, i: number) => {
              const dateStr = typeof df.forecast_date === 'string' ? df.forecast_date : (df.forecast_date as { value?: string })?.value ?? '';
              return {
              key: String(i),
              label: `${dateStr || 'Unknown date'} — ${df.weather_description || 'No description'}`,
              children: (
                <div>
                  <div style={{ marginBottom: 8 }}>
                    <strong>Weather:</strong> <span style={ws}>{df.weather_description || '-'}</span>
                  </div>
                  <div style={{ marginBottom: 4, fontWeight: 500 }}>Parameters:</div>
                  <Table
                    size="small"
                    dataSource={df.parameters || []}
                    columns={[
                      { title: 'Name', dataIndex: 'name', key: 'name', width: 180 },
                      { title: 'Value', dataIndex: 'value', key: 'value', render: (v: string | null) => v ?? '-' },
                      { title: 'Modified', dataIndex: 'modified', key: 'modified', width: 100, render: (v: number | null) => v ? dayjs(v).format('YYYY-MM-DD HH:mm') : '-' },
                    ]}
                    rowKey="name"
                    pagination={false}
                  />
                </div>
              ),
            };
            })}
          />
        </div>
      </div>
    );
  }

  if (type === 'minutely') {
    const fm = forecast as ForecastMinutely;
    return (
      <div style={{ padding: '8px 0' }}>
        <Descriptions column={1} size="small" bordered>
          <Descriptions.Item label="Create Datetime">{formatTimestamp(fm.create_datetime)}</Descriptions.Item>
          <Descriptions.Item label="Value Date">{formatTimestamp(fm.value_date)}</Descriptions.Item>
        </Descriptions>

        <div style={{ marginTop: 16 }}>
          <div style={{ fontWeight: 600, marginBottom: 8 }}>Minutely Forecasts</div>
          <Table
            size="small"
            dataSource={(fm.minutely_forecasts || []).map((mf: MinutelyForecast, i: number) => {
              const dt = getTimestampString(mf.forecast_datetime);
              return { ...mf, forecast_datetime: dt, _rowKey: `${dt}-${i}` };
            })}
            columns={[
              { title: 'Datetime', dataIndex: 'forecast_datetime', key: 'forecast_datetime', width: 160, render: (v: string) => (v ? formatTimestamp(v) : '-') },
              {
                title: 'Parameters',
                key: 'parameters',
                render: (_: unknown, row: MinutelyForecast) => (
                  <div style={{ ...ws, fontSize: '12px' }}>
                    {(row.parameters || []).map((p: { name: string; value: string | null }) => `${p.name}: ${p.value ?? '-'}`).join(', ')}
                  </div>
                ),
              },
            ]}
            rowKey="_rowKey"
            pagination={{ pageSize: 20, size: 'small' }}
            scroll={{ y: 400 }}
          />
        </div>
      </div>
    );
  }

  const fh = forecast as ForecastHourly;
  return (
    <div style={{ padding: '8px 0' }}>
      <Descriptions column={1} size="small" bordered>
        <Descriptions.Item label="Create Datetime">{formatTimestamp(fh.create_datetime)}</Descriptions.Item>
        <Descriptions.Item label="Value Date">{formatTimestamp(fh.value_date)}</Descriptions.Item>
        <Descriptions.Item label="Weather Summary">
          <div style={ws}>{fh.weather_summary || '-'}</div>
        </Descriptions.Item>
        <Descriptions.Item label="Alert Level">{fh.alert_level || '-'}</Descriptions.Item>
        <Descriptions.Item label="Previous Night">
          <div style={ws}>{fh.previous_night || '-'}</div>
        </Descriptions.Item>
      </Descriptions>

      {(fh.daily_metadata?.length ?? 0) > 0 && (
        <div style={{ marginTop: 16 }}>
          <div style={{ fontWeight: 600, marginBottom: 8 }}>Daily Metadata</div>
          <Collapse
            size="small"
            items={(fh.daily_metadata || []).map((dm: DailyMetadata, i: number) => ({
              key: String(i),
              label: dm.date,
              children: (
                <Descriptions column={1} size="small" bordered>
                  <Descriptions.Item label="Weather Summary"><div style={ws}>{dm.weather_summary || '-'}</div></Descriptions.Item>
                  <Descriptions.Item label="Alert Level">{dm.alert_level || '-'}</Descriptions.Item>
                  <Descriptions.Item label="Previous Night"><div style={ws}>{dm.previous_night || '-'}</div></Descriptions.Item>
                </Descriptions>
              ),
            }))}
          />
        </div>
      )}

      <div style={{ marginTop: 16 }}>
        <div style={{ fontWeight: 600, marginBottom: 8 }}>Hourly Forecasts</div>
        <Table
          size="small"
          dataSource={(fh.hourly_forecasts || []).map((hf: HourlyForecast, i: number) => {
            const dt = typeof hf.forecast_datetime === 'string' ? hf.forecast_datetime : (hf.forecast_datetime as { value?: string })?.value ?? '';
            return { ...hf, forecast_datetime: dt, _rowKey: `${dt}-${i}` };
          })}
          columns={[
            { title: 'Datetime', dataIndex: 'forecast_datetime', key: 'forecast_datetime', width: 160, render: (v: string) => (v ? formatTimestamp(v) : '-') },
            {
              title: 'Parameters',
              key: 'parameters',
              render: (_: unknown, row: HourlyForecast) => (
                <div style={{ ...ws, fontSize: '12px' }}>
                  {(row.parameters || []).map((p: { name: string; value: string | null }) => `${p.name}: ${p.value ?? '-'}`).join(', ')}
                </div>
              ),
            },
          ]}
          rowKey="_rowKey"
          pagination={{ pageSize: 10, size: 'small' }}
          scroll={{ y: 300 }}
        />
      </div>
    </div>
  );
}

// One row per ForecastDaily (all versions, sorted by value_date desc)
function flattenDailyForecasts(forecasts: ForecastDaily[]): Array<{ create_datetime: string; value_date: string; update_datetime: string | null; updated_by: string | null; _key: string; _forecast: ForecastDaily }> {
  if (!forecasts || forecasts.length === 0) return [];
  const sorted = [...forecasts].sort(
    (a, b) => new Date(b.value_date).getTime() - new Date(a.value_date).getTime()
  );
  return sorted.map((fd, i) => ({
    create_datetime: fd.create_datetime,
    value_date: fd.value_date,
    update_datetime: fd.update_datetime ?? null,
    updated_by: fd.updated_by ?? null,
    _key: `${fd.value_date}-${i}`,
    _forecast: fd,
  }));
}

// One row per ForecastHourly (parent hourly forecast, all versions)
function flattenHourlyForecasts(forecasts: ForecastHourly[]): Array<{ create_datetime: string; value_date: string; update_datetime: string | null; updated_by: string | null; _key: string; _forecast: ForecastHourly }> {
  if (!forecasts || forecasts.length === 0) return [];
  const sorted = [...forecasts].sort(
    (a, b) => new Date(b.value_date).getTime() - new Date(a.value_date).getTime()
  );
  return sorted.map((fh, i) => ({
    create_datetime: fh.create_datetime,
    value_date: fh.value_date,
    update_datetime: fh.update_datetime ?? null,
    updated_by: fh.updated_by ?? null,
    _key: `${fh.value_date}-${i}`,
    _forecast: fh,
  }));
}

// One row per ForecastMinutely (parent minutely forecast, all versions)
function flattenMinutelyForecasts(forecasts: ForecastMinutely[]): Array<{ create_datetime: string; value_date: string; _key: string; _forecast: ForecastMinutely }> {
  if (!forecasts || forecasts.length === 0) return [];
  const sorted = [...forecasts].sort(
    (a, b) => new Date(b.value_date).getTime() - new Date(a.value_date).getTime()
  );
  return sorted.map((fm, i) => ({
    create_datetime: fm.create_datetime,
    value_date: fm.value_date,
    _key: `${fm.value_date}-${i}`,
    _forecast: fm,
  }));
}

export const ForecastManagementPage = () => {
  const { selectedEvent } = useEvent();
  const [activeTab, setActiveTab] = useState<ForecastType>('daily');
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [dailyForecasts, setDailyForecasts] = useState<ForecastDaily[]>([]);
  const [hourlyForecasts, setHourlyForecasts] = useState<ForecastHourly[]>([]);
  const [minutelyForecasts, setMinutelyForecasts] = useState<ForecastMinutely[]>([]);
  const [loadingDaily, setLoadingDaily] = useState(false);
  const [loadingHourly, setLoadingHourly] = useState(false);
  const [loadingMinutely, setLoadingMinutely] = useState(false);
  const [errorDaily, setErrorDaily] = useState<string | null>(null);
  const [errorHourly, setErrorHourly] = useState<string | null>(null);
  const [errorMinutely, setErrorMinutely] = useState<string | null>(null);

  const circuitTimeZone = selectedEvent?.circuit?.time_zone || 'UTC';
  const source = selectedEvent ? getEventSource(selectedEvent) : 'prod';
  const supportsMinutelyForecasts = source !== 'legacy';

  const fetchDaily = useCallback(async () => {
    if (!selectedEvent) return;
    setLoadingDaily(true);
    setErrorDaily(null);
    try {
      const res = await forecastService.getForecastDaily({
        season_year: selectedEvent.season_year,
        circuit_id: selectedEvent.circuit_id,
        start_date: selectedEvent.start_date,
        source,
        include_deleted: includeDeleted,
      });
      setDailyForecasts(res.forecasts || []);
    } catch (err) {
      setErrorDaily(err instanceof Error ? err.message : 'Failed to fetch daily forecasts');
      setDailyForecasts([]);
    } finally {
      setLoadingDaily(false);
    }
  }, [selectedEvent, source, includeDeleted]);

  const fetchHourly = useCallback(async () => {
    if (!selectedEvent) return;
    setLoadingHourly(true);
    setErrorHourly(null);
    try {
      const res = await forecastService.getForecastHourly({
        season_year: selectedEvent.season_year,
        circuit_id: selectedEvent.circuit_id,
        start_date: selectedEvent.start_date,
        source,
        include_deleted: includeDeleted,
      });
      setHourlyForecasts(res.forecasts || []);
    } catch (err) {
      setErrorHourly(err instanceof Error ? err.message : 'Failed to fetch hourly forecasts');
      setHourlyForecasts([]);
    } finally {
      setLoadingHourly(false);
    }
  }, [selectedEvent, source, includeDeleted]);

  const fetchMinutely = useCallback(async () => {
    if (!selectedEvent || !supportsMinutelyForecasts) return;
    setLoadingMinutely(true);
    setErrorMinutely(null);
    try {
      const res = await forecastService.getForecastMinutely({
        season_year: selectedEvent.season_year,
        circuit_id: selectedEvent.circuit_id,
        start_date: selectedEvent.start_date,
        source,
        include_deleted: includeDeleted,
      });
      setMinutelyForecasts(res.forecasts || []);
    } catch (err) {
      setErrorMinutely(err instanceof Error ? err.message : 'Failed to fetch minutely forecasts');
      setMinutelyForecasts([]);
    } finally {
      setLoadingMinutely(false);
    }
  }, [selectedEvent, source, includeDeleted, supportsMinutelyForecasts]);

  useEffect(() => {
    if (selectedEvent && activeTab === 'daily') {
      fetchDaily();
    }
  }, [selectedEvent, activeTab, fetchDaily]);

  useEffect(() => {
    if (selectedEvent && activeTab === 'hourly') {
      fetchHourly();
    }
  }, [selectedEvent, activeTab, fetchHourly]);

  useEffect(() => {
    if (selectedEvent && activeTab === 'minutely') {
      fetchMinutely();
    }
  }, [selectedEvent, activeTab, fetchMinutely]);

  useEffect(() => {
    if (!supportsMinutelyForecasts && activeTab === 'minutely') {
      setActiveTab('daily');
    }
  }, [activeTab, supportsMinutelyForecasts]);

  const formatTimestamp = (ts: string) =>
    dayjs(ts).tz(circuitTimeZone).format('YYYY-MM-DD HH:mm');

  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [detailModalData, setDetailModalData] = useState<
    | { type: 'daily'; forecast: ForecastDaily }
    | { type: 'hourly'; forecast: ForecastHourly }
    | { type: 'minutely'; forecast: ForecastMinutely }
    | null
  >(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteConfirmFetching, setDeleteConfirmFetching] = useState(false);
  const [deleteConfirmForecast, setDeleteConfirmForecast] = useState<ForecastDaily | ForecastHourly | null>(null);
  const [deleteConfirmError, setDeleteConfirmError] = useState<string | null>(null);
  const [deleteDeleting, setDeleteDeleting] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const isLegacy = source === 'legacy';

  const filterByStatus = <T extends { _forecast: ForecastRecord }>(rows: T[]): T[] => {
    if (isLegacy || statusFilter === 'all') return rows;
    return rows.filter((row) => {
      const display = getDisplayStatus(row._forecast);
      if (display === 'deleted') return true;
      return display === statusFilter;
    });
  };

  const dailyRows = filterByStatus(flattenDailyForecasts(dailyForecasts));
  const hourlyRows = filterByStatus(flattenHourlyForecasts(hourlyForecasts));
  const minutelyRows = filterByStatus(flattenMinutelyForecasts(minutelyForecasts));

  type DailyRow = { create_datetime: string; value_date: string; update_datetime: string | null; updated_by: string | null; _key: string; _forecast: ForecastDaily };
  type HourlyRow = { create_datetime: string; value_date: string; update_datetime: string | null; updated_by: string | null; _key: string; _forecast: ForecastHourly };
  type MinutelyRow = { create_datetime: string; value_date: string; _key: string; _forecast: ForecastMinutely };

  const handleRowClick = (type: ForecastType, forecast: ForecastRecord) => {
    if (type === 'daily') {
      setDetailModalData({ type: 'daily', forecast: forecast as ForecastDaily });
    } else if (type === 'hourly') {
      setDetailModalData({ type: 'hourly', forecast: forecast as ForecastHourly });
    } else {
      setDetailModalData({ type: 'minutely', forecast: forecast as ForecastMinutely });
    }
    setDetailModalOpen(true);
  };

  const handleOpenDeleteConfirm = () => {
    if (!detailModalData) return;
    setDeleteConfirmOpen(true);
    setDeleteConfirmForecast(null);
    setDeleteConfirmError(null);
    setDeleteConfirmFetching(true);
  };

  useEffect(() => {
    if (!deleteConfirmOpen || !detailModalData || !selectedEvent) return;
    if (detailModalData.type === 'minutely') return;

    const run = async () => {
      const f = detailModalData.forecast;
      const valueDate = typeof f.value_date === 'string' ? f.value_date : (f.value_date as { value?: string })?.value ?? '';
      const createDatetime = typeof f.create_datetime === 'string' ? f.create_datetime : (f.create_datetime as { value?: string })?.value ?? '';

      try {
        if (detailModalData.type === 'daily') {
          const res = await forecastService.verifyForecastDaily({
            season_year: selectedEvent.season_year,
            circuit_id: selectedEvent.circuit_id,
            start_date: selectedEvent.start_date,
            value_date: valueDate,
            create_datetime: createDatetime,
            source,
          });
          setDeleteConfirmForecast(res.forecast);
          setDeleteConfirmError(null);
        } else {
          const res = await forecastService.verifyForecastHourly({
            season_year: selectedEvent.season_year,
            circuit_id: selectedEvent.circuit_id,
            start_date: selectedEvent.start_date,
            value_date: valueDate,
            create_datetime: createDatetime,
            source,
          });
          setDeleteConfirmForecast(res.forecast);
          setDeleteConfirmError(null);
        }
      } catch (err) {
        setDeleteConfirmError(err instanceof Error ? err.message : 'Failed to fetch forecast');
        setDeleteConfirmForecast(null);
      } finally {
        setDeleteConfirmFetching(false);
      }
    };

    run();
  }, [deleteConfirmOpen, detailModalData, selectedEvent, source]);

  const handleDeleteForecast = async () => {
    if (!selectedEvent || !deleteConfirmForecast) return;
    const isDaily = detailModalData?.type === 'daily';
    const fd = deleteConfirmForecast as ForecastDaily;
    const fh = deleteConfirmForecast as ForecastHourly;

    setDeleteDeleting(true);
    try {
      if (isDaily) {
        await forecastService.deleteForecastDaily({
          season_year: selectedEvent.season_year,
          circuit_id: selectedEvent.circuit_id,
          start_date: selectedEvent.start_date,
          value_date: fd.value_date,
          create_datetime: fd.create_datetime,
          source,
        });
        message.success('Daily forecast deleted');
        fetchDaily();
      } else {
        await forecastService.deleteForecastHourly({
          season_year: selectedEvent.season_year,
          circuit_id: selectedEvent.circuit_id,
          start_date: selectedEvent.start_date,
          value_date: fh.value_date,
          create_datetime: fh.create_datetime,
          source,
        });
        message.success('Hourly forecast deleted');
        fetchHourly();
      }
      setDeleteConfirmOpen(false);
      setDetailModalOpen(false);
      setDetailModalData(null);
      setDeleteConfirmForecast(null);
    } catch (err) {
      message.error(err instanceof Error ? err.message : 'Failed to soft-delete forecast');
    } finally {
      setDeleteDeleting(false);
    }
  };

  const handleRestoreForecast = async () => {
    if (!selectedEvent || !detailModalData) return;
    if (detailModalData.type === 'minutely') return;
    const f = detailModalData.forecast;
    const valueDate = typeof f.value_date === 'string' ? f.value_date : (f.value_date as { value?: string })?.value ?? '';
    const createDatetime = typeof f.create_datetime === 'string' ? f.create_datetime : (f.create_datetime as { value?: string })?.value ?? '';

    setRestoring(true);
    try {
      await forecastService.setForecastDeleted(detailModalData.type, {
        season_year: selectedEvent.season_year,
        circuit_id: selectedEvent.circuit_id,
        start_date: selectedEvent.start_date,
        value_date: valueDate,
        create_datetime: createDatetime,
        deleted: false,
        source,
      });
      message.success(`${detailModalData.type === 'daily' ? 'Daily' : 'Hourly'} forecast restored`);
      setDetailModalOpen(false);
      setDetailModalData(null);
      if (detailModalData.type === 'daily') fetchDaily();
      else fetchHourly();
    } catch (err) {
      message.error(err instanceof Error ? err.message : 'Failed to restore forecast');
    } finally {
      setRestoring(false);
    }
  };

  const handleUpdateStatus = async (newStatus: 'draft' | 'published') => {
    if (!selectedEvent || !detailModalData) return;
    if (detailModalData.type === 'minutely') return;
    const f = detailModalData.forecast;
    const valueDate = typeof f.value_date === 'string' ? f.value_date : (f.value_date as { value?: string })?.value ?? '';
    const createDatetime = typeof f.create_datetime === 'string' ? f.create_datetime : (f.create_datetime as { value?: string })?.value ?? '';

    setUpdatingStatus(true);
    try {
      await forecastService.updateForecastStatus(detailModalData.type, {
        season_year: selectedEvent.season_year,
        circuit_id: selectedEvent.circuit_id,
        start_date: selectedEvent.start_date,
        value_date: valueDate,
        create_datetime: createDatetime,
        status: newStatus,
        source,
      });
      message.success(`Forecast ${newStatus === 'published' ? 'published' : 'unpublished'}`);
      setDetailModalOpen(false);
      setDetailModalData(null);
      if (detailModalData.type === 'daily') fetchDaily();
      else fetchHourly();
    } catch (err) {
      message.error(err instanceof Error ? err.message : 'Failed to update forecast status');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const dailyColumns: ColumnsType<DailyRow> = [
    {
      title: 'Create Datetime',
      dataIndex: 'create_datetime',
      key: 'create_datetime',
      width: 160,
      render: (v: string) => formatTimestamp(v),
    },
    {
      title: 'Value Date',
      dataIndex: 'value_date',
      key: 'value_date',
      width: 160,
      render: (v: string) => formatTimestamp(v),
    },
    {
      title: 'Updated At',
      dataIndex: 'update_datetime',
      key: 'update_datetime',
      width: 160,
      render: (v: string | null) => v ? formatTimestamp(v) : '-',
    },
    {
      title: 'Updated By',
      dataIndex: 'updated_by',
      key: 'updated_by',
      width: 140,
      render: (v: string | null) => v || '-',
    },
    {
      title: 'Status',
      key: 'status',
      width: 100,
      render: (_: unknown, row: DailyRow) => renderStatusTag(row._forecast, isLegacy),
    },
  ];

  const hourlyColumns: ColumnsType<HourlyRow> = [
    {
      title: 'Create Datetime',
      dataIndex: 'create_datetime',
      key: 'create_datetime',
      width: 160,
      render: (v: string) => formatTimestamp(v),
    },
    {
      title: 'Value Date',
      dataIndex: 'value_date',
      key: 'value_date',
      width: 160,
      render: (v: string) => formatTimestamp(v),
    },
    {
      title: 'Updated At',
      dataIndex: 'update_datetime',
      key: 'update_datetime',
      width: 160,
      render: (v: string | null) => v ? formatTimestamp(v) : '-',
    },
    {
      title: 'Updated By',
      dataIndex: 'updated_by',
      key: 'updated_by',
      width: 140,
      render: (v: string | null) => v || '-',
    },
    {
      title: 'Status',
      key: 'status',
      width: 100,
      render: (_: unknown, row: HourlyRow) => renderStatusTag(row._forecast, isLegacy),
    },
  ];

  const minutelyColumns: ColumnsType<MinutelyRow> = [
    {
      title: 'Create Datetime',
      dataIndex: 'create_datetime',
      key: 'create_datetime',
      width: 160,
      render: (v: string) => formatTimestamp(v),
    },
    {
      title: 'Value Date',
      dataIndex: 'value_date',
      key: 'value_date',
      width: 160,
      render: (v: string) => formatTimestamp(v),
    },
    {
      title: 'Status',
      key: 'status',
      width: 100,
      render: (_: unknown, row: MinutelyRow) => renderStatusTag(row._forecast, isLegacy),
    },
  ];

  if (!selectedEvent) {
    return (
      <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden', padding: '8px' }}>
        <Card style={{ flex: 1 }}>
          <Empty description="Please select an event to view forecasts" />
        </Card>
      </div>
    );
  }

  const tabItems = [
    {
      key: 'daily',
      label: 'Daily Forecasts',
      children: (
        <Card
          title={
            <Space>
              <span>Daily Forecasts</span>
              {dailyForecasts.length > 0 && (
                <Tag color="blue">
                  {dailyForecasts.length} forecast{dailyForecasts.length !== 1 ? 's' : ''}
                </Tag>
              )}
            </Space>
          }
          extra={
            <Button
              icon={<ReloadOutlined spin={loadingDaily} />}
              onClick={fetchDaily}
              size="small"
            >
              Refresh
            </Button>
          }
        >
          {errorDaily && (
            <div style={{ marginBottom: 12, color: '#f5222d' }}>Error: {errorDaily}</div>
          )}
          <Table
            dataSource={dailyRows}
            columns={dailyColumns}
            rowKey="_key"
            loading={loadingDaily}
            size="small"
            scroll={{ y: 'calc(100vh - 380px)' }}
            pagination={{ pageSize: 10, showSizeChanger: true, showTotal: (t) => `${t} rows` }}
            locale={{ emptyText: 'No daily forecasts found' }}
            onRow={(record) => ({
              onClick: () => handleRowClick('daily', record._forecast),
              style: { cursor: 'pointer' },
            })}
          />
        </Card>
      ),
    },
    {
      key: 'hourly',
      label: 'Hourly Forecasts',
      children: (
        <Card
          title={
            <Space>
              <span>Hourly Forecasts</span>
              {hourlyForecasts.length > 0 && (
                <Tag color="blue">
                  {hourlyForecasts.length} forecast{hourlyForecasts.length !== 1 ? 's' : ''}
                </Tag>
              )}
            </Space>
          }
          extra={
            <Button
              icon={<ReloadOutlined spin={loadingHourly} />}
              onClick={fetchHourly}
              size="small"
            >
              Refresh
            </Button>
          }
        >
          {errorHourly && (
            <div style={{ marginBottom: 12, color: '#f5222d' }}>Error: {errorHourly}</div>
          )}
          <Table
            dataSource={hourlyRows}
            columns={hourlyColumns}
            rowKey="_key"
            loading={loadingHourly}
            size="small"
            scroll={{ y: 'calc(100vh - 380px)' }}
            pagination={{ pageSize: 20, showSizeChanger: true, showTotal: (t) => `${t} rows` }}
            locale={{ emptyText: 'No hourly forecasts found' }}
            onRow={(record) => ({
              onClick: () => handleRowClick('hourly', record._forecast),
              style: { cursor: 'pointer' },
            })}
          />
        </Card>
      ),
    },
    ...(supportsMinutelyForecasts ? [{
      key: 'minutely',
      label: 'Minutely Forecasts',
      children: (
        <Card
          title={
            <Space>
              <span>Minutely Forecasts</span>
              {minutelyForecasts.length > 0 && (
                <Tag color="blue">
                  {minutelyForecasts.length} forecast{minutelyForecasts.length !== 1 ? 's' : ''}
                </Tag>
              )}
            </Space>
          }
          extra={
            <Button
              icon={<ReloadOutlined spin={loadingMinutely} />}
              onClick={fetchMinutely}
              size="small"
            >
              Refresh
            </Button>
          }
        >
          {errorMinutely && (
            <div style={{ marginBottom: 12, color: '#f5222d' }}>Error: {errorMinutely}</div>
          )}
          <Table
            dataSource={minutelyRows}
            columns={minutelyColumns}
            rowKey="_key"
            loading={loadingMinutely}
            size="small"
            scroll={{ y: 'calc(100vh - 380px)' }}
            pagination={{ pageSize: 20, showSizeChanger: true, showTotal: (t) => `${t} rows` }}
            locale={{ emptyText: 'No minutely forecasts found' }}
            onRow={(record) => ({
              onClick: () => handleRowClick('minutely', record._forecast),
              style: { cursor: 'pointer' },
            })}
          />
        </Card>
      ),
    }] : []),
  ];

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden', padding: 0 }}>
      <Card
        style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', borderRadius: 0 }}
        styles={{ body: { flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', padding: '8px 12px' } }}
      >
        <div style={{ marginBottom: 8, flexShrink: 0, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
          <Text strong>{selectedEvent.circuit?.display_name}</Text>
          <Text type="secondary">
            {selectedEvent.start_date} – {selectedEvent.end_date}
          </Text>
          {source !== 'prod' && (
            <Tag color="orange">{source}</Tag>
          )}
          <Checkbox
            checked={includeDeleted}
            onChange={(e) => setIncludeDeleted(e.target.checked)}
          >
            Include deleted
          </Checkbox>
          {!isLegacy && (
            <Select
              value={statusFilter}
              onChange={setStatusFilter}
              size="small"
              style={{ width: 130 }}
              options={[
                { value: 'all', label: 'All statuses' },
                { value: 'draft', label: 'Draft' },
                { value: 'published', label: 'Published' },
              ]}
            />
          )}
        </div>
        <Tabs
          activeKey={activeTab}
          onChange={(k) => setActiveTab(k as ForecastType)}
          items={tabItems}
          style={{ flex: 1, overflow: 'hidden' }}
        />

        <Modal
          title={detailModalData ? `${getForecastTypeLabel(detailModalData.type)} Forecast — ${formatTimestamp(detailModalData.forecast.value_date)}` : 'Forecast Details'}
          open={detailModalOpen}
          onCancel={() => setDetailModalOpen(false)}
          footer={
            <Space>
              <Button onClick={() => setDetailModalOpen(false)}>Close</Button>
              {!isLegacy && detailModalData && detailModalData.type !== 'minutely' && (
                detailModalData.forecast.deleted ? (
                  <Button type="primary" loading={restoring} onClick={handleRestoreForecast}>
                    Restore
                  </Button>
                ) : (
                  <>
                    {getDisplayStatus(detailModalData.forecast) === 'draft' ? (
                      <Button
                        type="primary"
                        loading={updatingStatus}
                        onClick={() => handleUpdateStatus('published')}
                        style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
                      >
                        Publish
                      </Button>
                    ) : (
                      <Button
                        loading={updatingStatus}
                        onClick={() => handleUpdateStatus('draft')}
                        style={{ color: '#d48806', borderColor: '#d48806' }}
                      >
                        Unpublish
                      </Button>
                    )}
                    <Button danger onClick={handleOpenDeleteConfirm}>
                      Delete
                    </Button>
                  </>
                )
              )}
            </Space>
          }
          width={800}
          styles={{ body: { maxHeight: '70vh', overflowY: 'auto' } }}
        >
          {detailModalData && (
            <ForecastDetailContent
              type={detailModalData.type}
              forecast={detailModalData.forecast}
              formatTimestamp={formatTimestamp}
              wrapStyle={wrapStyle}
            />
          )}
        </Modal>

        <Modal
          title="Confirm Soft Delete"
          open={deleteConfirmOpen}
          onCancel={() => setDeleteConfirmOpen(false)}
          footer={
            <Space>
              <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
              <Button
                danger
                loading={deleteDeleting}
                disabled={!deleteConfirmForecast || deleteConfirmFetching}
                onClick={handleDeleteForecast}
              >
                Confirm Soft Delete
              </Button>
            </Space>
          }
          width={700}
        >
          {deleteConfirmFetching ? (
            <div style={{ padding: 24, textAlign: 'center' }}>
              <Text type="secondary">Fetching forecast from server to verify...</Text>
            </div>
          ) : deleteConfirmError ? (
            <div style={{ padding: 24 }}>
              <Text type="danger">{deleteConfirmError}</Text>
              <div style={{ marginTop: 8 }}>
                <Text type="secondary">The forecast cannot be deleted. Close this dialog and try again if needed.</Text>
              </div>
            </div>
          ) : deleteConfirmForecast ? (
            <div style={{ padding: '8px 0' }}>
              <Text strong>Verify the forecast below before soft-deleting. You can restore it later from the management page.</Text>
              <div style={{ marginTop: 16, display: 'flex', gap: 24 }}>
                <div>
                  <Text type="secondary">Create Datetime:</Text>{' '}
                  <Text>{formatTimestamp(deleteConfirmForecast.create_datetime)}</Text>
                </div>
                <div>
                  <Text type="secondary">Value Date:</Text>{' '}
                  <Text>{formatTimestamp(deleteConfirmForecast.value_date)}</Text>
                </div>
              </div>
            </div>
          ) : null}
        </Modal>
      </Card>
    </div>
  );
};

export default ForecastManagementPage;
